import { TFunction } from 'i18next'

import { getBill } from '@features/bills/query-api'
import { fetchExternalInvoice } from '@features/invoices/query-api'
import { BookkeepingItemType } from '@features/reconciliation/enums/bookkeepingItemType'
import { BookkeepingItem } from '@features/reconciliation/types/bookkeepingItem'
import { mapBill, mapExternalInvoice, mapInvoice, mapPosting } from '@features/reconciliation/utils/mappers'
import { fetchBalanceModifiers } from '@features/transactions/query-api'
import { Posting } from '@modules-deprecated/bankReconciliation/types'
import { getInvoice } from '@views/invoices/query-api'

import { fetchTransactionById } from '../query-api'
import { BalanceModifier } from '../types/balanceModifier'
import { parseSubjectReference } from './parseSubjectReference'

/**
 * Fetches the document referenced by a balance modifier's subject and maps it to a
 * BookkeepingItem. The mapped amount is overridden with the modifier's amount — the document's
 * own `balance` is 0 once fully paid, so the modifier holds the actual amount applied to it.
 * Returns null for unknown subjects or when the document fetch fails.
 */
const resolveDocument = async (
  modifier: BalanceModifier,
  contactsMap: Map<string, string>,
  t: TFunction,
): Promise<BookkeepingItem | null> => {
  const parsed = parseSubjectReference(modifier.subjectReference)

  if (!parsed) {
    return null
  }

  try {
    let item: BookkeepingItem

    if (parsed.type === BookkeepingItemType.Bill) {
      const { bill } = await getBill({ billId: parsed.id })
      item = mapBill(bill, contactsMap, t)
    } else if (parsed.type === BookkeepingItemType.Invoice) {
      const { invoice } = await getInvoice({ invoiceId: parsed.id })
      item = mapInvoice(invoice, contactsMap, t)
    } else {
      const { externalInvoice } = await fetchExternalInvoice(parsed.id)
      item = mapExternalInvoice(externalInvoice, contactsMap, t)
    }

    return modifier.amount !== null ? { ...item, amount: Math.abs(modifier.amount) } : item
  } catch {
    return null
  }
}

/**
 * Resolves a matched ledger posting back to the original documents it paid. Climbs
 * posting → transaction → bankPayment → balanceModifiers → subjects. A single bank payment can
 * settle several documents, so a bankPayment posting expands into every document its modifiers
 * reference (not just one). Falls back to the posting itself for daybook/manual entries or when
 * no document can be resolved.
 */
const resolvePosting = async (
  posting: Posting,
  contactsMap: Map<string, string>,
  t: TFunction,
): Promise<BookkeepingItem[]> => {
  try {
    const { transaction } = await fetchTransactionById(posting.transactionId)
    const { originatorReference } = transaction

    if (!originatorReference?.startsWith('bankPayment:')) {
      return [mapPosting(posting, t)]
    }

    const { balanceModifiers } = await fetchBalanceModifiers(originatorReference)
    const documents = await Promise.all(balanceModifiers.map((modifier) => resolveDocument(modifier, contactsMap, t)))
    const resolved = documents.filter((item): item is BookkeepingItem => item !== null)

    return resolved.length > 0 ? resolved : [mapPosting(posting, t)]
  } catch {
    return [mapPosting(posting, t)]
  }
}

/**
 * Resolves every matched posting back to its source documents and returns a de-duplicated list.
 * A combined match can surface several postings sharing one bank payment, which would otherwise
 * repeat the same documents — so results are de-duplicated by id.
 */
export const resolveMatchedItems = async (
  postings: Posting[],
  contactsMap: Map<string, string>,
  t: TFunction,
): Promise<BookkeepingItem[]> => {
  const results = await Promise.all(postings.map((posting) => resolvePosting(posting, contactsMap, t)))

  return Array.from(new Map(results.flat().map((item) => [item.id, item])).values())
}
